import { createClient } from '@supabase/supabase-js';

// Validate environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate that environment variables are properly set
if (!supabaseUrl) {
  console.error('Missing VITE_SUPABASE_URL environment variable');
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!supabaseAnonKey) {
  console.error('Missing VITE_SUPABASE_ANON_KEY environment variable');
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable');
}

// Validate URL format
try {
  new URL(supabaseUrl);
} catch (error) {
  console.error(`Invalid VITE_SUPABASE_URL format: ${supabaseUrl}`);
  throw new Error(`Invalid VITE_SUPABASE_URL format: ${supabaseUrl}. Expected format: https://your-project-id.supabase.co`);
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'X-Client-Info': 'payrolli@1.0.0'
    }
  },
  db: {
    schema: 'public'
  }
});

// Add retry logic for database operations
export const withRetry = async <T>(
  operation: () => Promise<T>,
  maxAttempts = 3,
  delayMs = 1000
): Promise<T> => {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      if (attempt === maxAttempts) break;
      
      await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
    }
  }
  
  throw lastError;
};

// Add auth state change listener with error handling
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'PASSWORD_RECOVERY') {
    window.location.href = '/reset-password';
  }

  if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
    window.location.href = '/';
  }
});

// Helper function to check network connectivity
export const checkNetworkConnectivity = async (): Promise<boolean> => {
  try {
    // First check if we're online at all
    if (!navigator.onLine) {
      return false;
    }

    // Try to connect to Supabase directly
    const { data, error } = await supabase
      .from('employees')
      .select('id')
      .limit(1);

    if (error) {
      // If there's an error but it's an auth or permission error,
      // we're still connected - just not authenticated
      if (error.code === '401' || error.code === '403') {
        return true;
      }
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Network connectivity check failed:', error);
    
    // If we get here, try one more fallback check to a reliable CDN
    try {
      const response = await fetch('https://cdn.jsdelivr.net/npm/react@18/package.json', {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store'
      });
      return true;
    } catch (e) {
      return false;
    }
  }
};